import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessorConnector } from '../control-value-accessor-connector';
import { NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { BehaviorSubject, debounce, Observable, of, tap, timer } from 'rxjs';
import {
  MAT_TOOLTIP_DEFAULT_OPTIONS,
  MatTooltipDefaultOptions,
} from '@angular/material/tooltip';
import { LingFileEntity } from '../../../../core/models/ling-file-entity';
import { AuthService } from '../../../../core/services/api/auth.service';
import { FilesService } from '../../../../core/services/api/files.service';
import { environment } from '../../../../../environments/environment';
import {
  DocumentMimeType,
  FileOperations,
  getDisplayedExtensions,
  ImageMimeType,
  LingMediaMimeType,
  LingMimeType,
  VideoMimeType,
} from '../../../../core/enums';
import { SnackBarService } from '../../../../core/services/snack-bar.service';
import { bytesToMb, mbToBytes } from '../../../../core/utils';
import { FeathersService } from '../../../../core/services/api/feathers.service';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { AddFileDialogComponent } from '../../dialog/add-file-dialog/add-file-dialog.component';

const myCustomTooltipDefaults: MatTooltipDefaultOptions = {
  showDelay: 0,
  hideDelay: 0,
  touchendHideDelay: 1500,
  disableTooltipInteractivity: true,
};

@Component({
  selector: 'ling-file-input',
  templateUrl: './file-input.component.html',
  styleUrls: ['./file-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FileInputComponent,
      multi: true,
    },
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: myCustomTooltipDefaults },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileInputComponent
  extends ControlValueAccessorConnector
  implements OnChanges
{
  @ViewChild('fileInput') fileInputRef!: ElementRef;
  @Input() info = '';
  @Input() label = '';
  @Input() removeButton = false;
  @Input() forceDelete = false;
  @Input() maxFiles: number = 4;
  @Input() maxFileSize: number = mbToBytes(200);
  @Input() limit?: number;
  @Input() type: 'certificate' | 'avatar' = 'certificate';
  @Input() isEditButton = false;
  @Input() editButtonInfo = '';
  @Input() allowSvg: boolean | null | undefined = false;
  @Output() removeButtonPress = new EventEmitter();
  @Output() editButtonPress = new EventEmitter();
  @Output() valueChange = new EventEmitter<File[] | LingFileEntity[]>();
  readonly acceptImageDefault = [ImageMimeType.JPEG, ImageMimeType.PNG];
  readonly acceptVideoDefault = [
    VideoMimeType.MP4,
    VideoMimeType.MOV,
    VideoMimeType.AVI,
    VideoMimeType.FLV,
    VideoMimeType.MKV,
  ];
  @Input() acceptMimeType: LingMimeType[] = [
    ...this.acceptImageDefault,
    ...this.acceptVideoDefault,
  ];

  private _disabledInput = false;

  // Cannot use [disabled] directive on input because it's overrode by forms module directive.
  @Input() set disabledInput(isDisabled: boolean) {
    this._disabledInput = isDisabled;

    if (this.fileInputRef?.nativeElement) {
      if (isDisabled) {
        this.fileInputRef.nativeElement.setAttribute('disabled', true);
      } else {
        this.fileInputRef.nativeElement.removeAttribute('disabled');
      }
    }
  }

  get disabledInput() {
    return this._disabledInput;
  }

  documentMimeType = DocumentMimeType;
  leftUploadedDuringThisInstance: LingFileEntity[] = [];
  currentlyUploadingFiles: File[] = [];
  isDragging$ = new BehaviorSubject<boolean>(false);
  pipedIsDragging$ = this.isDragging$.pipe(
    debounce((state) => (state ? of(0) : timer(50)))
  );
  localLinksMap: SafeUrl[] = [];
  isFocused = false;
  filesLoading = false;
  toBeDeleted: LingFileEntity[] = [];

  @HostListener('focusin', ['$event'])
  onFocusIn(event: FocusEvent) {
    if (event.target !== this.removeInputButtonElement && !this.disabledInput) {
      this.isFocused = true;
    }
  }

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: any) {
    if (!this.elementRef.nativeElement.contains(target)) {
      this.isFocused = false;
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case 'Tab': {
        this.onTabPressed();
        break;
      }
    }
  }

  @HostListener('window:dragover', ['$event']) onGlobalDragOver(
    event: DragEvent
  ) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      event.preventDefault();
    }

    if (event.dataTransfer?.types.includes('Files')) {
      this.isDragging$.next(true);
    }
  }

  @HostListener('window:dragleave', ['$event']) onGlobalDragLeave() {
    this.isDragging$.next(false);
  }

  @HostListener('window:drop', ['$event']) onGlobalDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging$.next(false);
  }

  @HostListener('drop', ['$event']) onDrop(event: DragEvent) {
    this.isDragging$.next(false);
    if (event.dataTransfer && event.dataTransfer.files.length) {
      this.processFiles({
        files: Array.from(event.dataTransfer.files),
        operator: FileOperations.Add,
      });
    }
  }

  @HostListener('dragover', ['$event']) onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  constructor(
    injector: Injector,
    private sanitizer: DomSanitizer,
    private auth: AuthService,
    private feathers: FeathersService,
    private cd: ChangeDetectorRef,
    private message: SnackBarService,
    private elementRef: ElementRef,
    private fileService: FilesService,
    private translateService: TranslateService,
    private matDialog: MatDialog
  ) {
    super(injector, translateService);
  }

  override writeValue(value: any) {
    let incomingValue = value;
    if (!Array.isArray(incomingValue)) {
      incomingValue = [];
      if (value) {
        incomingValue.push(value);
      }
    }
    super.writeValue(incomingValue);
    this.updateLinksMap();
    if (this.feathers.me$.value) {
      this.uploadPendingFiles();
    }
  }

  get acceptMimeTypeComplete(): LingMimeType[] {
    return [
      ...this.acceptMimeType,
      this.allowSvg ? 'image/svg+xml' : null,
    ].filter(Boolean) as LingMimeType[];
  }

  get acceptListString(): string {
    return this.acceptMimeTypeComplete.join(', ').toLowerCase();
  }

  get acceptListExtensionsString(): string {
    return this.acceptMimeTypeComplete
      .map(getDisplayedExtensions)
      .join(', ')
      .toUpperCase();
  }

  get removeInputButtonElement(): HTMLButtonElement | null {
    return (this.elementRef.nativeElement as HTMLElement).querySelector(
      '#removeInputButton'
    );
  }

  get isRequired(): boolean {
    return this.control.hasValidator(Validators.required);
  }

  override ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
    if (this.type === 'avatar') {
      this.maxFiles = 1;
    }
    if (changes['acceptMimeType']) {
      this.acceptMimeType = this.acceptMimeType.filter((mimeType) => {
        const split = mimeType.split('/');
        return split && split.length === 2;
      });
    }
  }

  canFit(amount: number): boolean {
    return !!(
      this.maxFiles &&
      this.maxFiles > 0 &&
      amount <= this.maxFiles - this.control.value.length
    );
  }

  fileIsCurrentlyUploading(fileToCheck: File): boolean {
    return !!this.currentlyUploadingFiles.find((file) => file === fileToCheck);
  }

  isVanillaFile(file: File | LingFileEntity): boolean {
    return file instanceof File;
  }

  isAllowedFileExt(file: File): boolean {
    return this.acceptMimeTypeComplete
      .map((mimeType) => mimeType.toLowerCase())
      .includes(file.type.toLowerCase() ?? '');
  }

  isAllowedFileSize(file: File): boolean {
    return file.size < this.maxFileSize;
  }

  isFileCanBeShown(file: File | LingFileEntity) {
    const playableMimeTypes = [
      VideoMimeType.MP4,
      ImageMimeType.JPEG,
      ImageMimeType.PNG,
      DocumentMimeType.PDF,
    ];

    if (file instanceof File) {
      return playableMimeTypes.includes(file.type as any);
    }

    return (
      !file.isProcessing &&
      playableMimeTypes.includes(file.file?.mimetype as any)
    );
  }

  isAppropriateBaseMimeType(
    mimeType: string,
    file: File | LingFileEntity
  ): boolean {
    if (file instanceof File) {
      return this.acceptMimeTypeComplete
        .filter((acceptedMimeType) =>
          acceptedMimeType.toLowerCase().startsWith(mimeType)
        )
        .includes((file.type as LingMediaMimeType) ?? ('' as any));
    }
    return this.acceptMimeTypeComplete
      .filter((acceptedMimeType) =>
        acceptedMimeType.toLowerCase().startsWith(mimeType)
      )
      .includes((file.file?.mimetype ?? '') as any);
  }

  updateLinksMap(): void {
    if (!this.control.value) {
      return;
    }
    this.localLinksMap =
      this.control.value?.map((file: File | LingFileEntity | null) => {
        if (!file) {
          return '';
        }
        if (file instanceof File) {
          return this.getSafeFileUrl(file);
        }
        return environment.url + '/files/' + file.file?.filename;
      }) ?? [];
    this.cd.markForCheck();
  }

  getSafeFileUrl(file: File): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
  }

  processFiles(operation: {
    files: (File | LingFileEntity)[];
    operator: FileOperations;
  }) {
    if (this.disabledInput) {
      return;
    }
    if (!this.control.value) {
      this.control.setValue([]);
    }
    switch (operation.operator) {
      case FileOperations.Add: {
        const entityFiles = operation.files.filter(
          (f): f is LingFileEntity => !(f instanceof File)
        );
        if (entityFiles.length) {
          if (!this.canFit(entityFiles.length)) {
            this.message.warn(
              this.translateService.instant('snackbar.warn.maxFiles', {
                maxAmount: this.maxFiles,
              })
            );
            return;
          }
          this.addFilesOffline(entityFiles);
          break;
        }
        const newFiles = operation.files as File[];
        const acceptedFiles = newFiles.filter(
          (file) => this.isAllowedFileExt(file) && this.isAllowedFileSize(file)
        );
        const discardedByType = newFiles.filter(
          (file) => !this.isAllowedFileExt(file)
        );
        const discardedBySize = newFiles.filter(
          (file) => !this.isAllowedFileSize(file)
        );

        if (discardedByType.length) {
          const fileNamesString = discardedByType
            .map((file) => file.name)
            .join(', ');
          const discardedByExtension =
            discardedByType.length > 1
              ? this.translateService.instant(
                  'snackbar.warn.filesDiscardedByTypeMultiple',
                  {
                    fileNames: fileNamesString,
                    acceptedFileTypes: this.acceptListExtensionsString,
                  }
                )
              : this.translateService.instant(
                  'snackbar.warn.filesDiscardedByTypeSingle',
                  {
                    fileName: fileNamesString,
                    acceptedFileTypes: this.acceptListExtensionsString,
                  }
                );
          this.message.warn(discardedByExtension);
        }
        if (discardedBySize.length) {
          const fileNamesString = discardedBySize
            .map((file) => file.name)
            .join(', ');
          const discardedFilesMessage =
            discardedBySize.length > 1
              ? this.translateService.instant(
                  'snackbar.warn.filesDiscardedBySizeMultiple',
                  {
                    fileNames: fileNamesString,
                    allowedFileSize: bytesToMb(this.maxFileSize),
                  }
                )
              : this.translateService.instant(
                  'snackbar.warn.filesDiscardedBySizeSingle',
                  {
                    fileName: fileNamesString,
                    allowedFileSize: bytesToMb(this.maxFileSize),
                  }
                );
          this.message.warn(discardedFilesMessage);
        }

        if (this.type === 'avatar') {
          if (acceptedFiles.length > 1) {
            this.message.warn(
              this.translateService.instant(
                'snackbar.warn.avatarMultipleFilesNotAllowed'
              )
            );
            return;
          }
        } else if (!this.canFit(acceptedFiles.length)) {
          this.message.warn(
            this.translateService.instant('snackbar.warn.maxFiles', {
              maxAmount: this.maxFiles,
            })
          );
          return;
        }
        this.addFilesOffline(acceptedFiles);
        if (this.feathers.me$.value) {
          this.uploadPendingFiles();
        }
        break;
      }
      case FileOperations.Delete: {
        this.removeFilesOffline(operation.files);
        if (this.feathers.me$.value) {
          const uploadedFiles = operation.files.filter(
            (file: any) => !(file instanceof File)
          ) as LingFileEntity[];
          uploadedFiles.forEach((file) => this.markFileToBeDeleted(file));
        }
        break;
      }
    }
  }

  addFilesOffline(newFiles: (File | LingFileEntity)[]): void {
    if (this.type === 'avatar') {
      this.processFiles({
        files: this.control.value,
        operator: FileOperations.Delete,
      });
      this.updateControlValue(newFiles as any[]);
    } else {
      this.updateControlValue([...this.control.value, ...newFiles]);
    }
  }

  uploadPendingFiles(): void {
    if (!this.control.value) {
      this.control.setValue([]);
    }
    const pendingFiles = this.control.value
      .filter((file: any) => file instanceof File)
      .filter((file: File) => !this.fileIsCurrentlyUploading(file));
    if (!pendingFiles?.length) {
      return;
    }
    this.filesLoading = true;
    this.currentlyUploadingFiles.push(...pendingFiles);
    const existingUploadedFiles = this.control.value.filter(
      (file: any) => !(file instanceof File)
    );
    this.fileService.uploadMedia(pendingFiles).subscribe({
      next: (newUploads) => {
        this.leftUploadedDuringThisInstance.push(...newUploads);
        this.updateControlValue([...existingUploadedFiles, ...newUploads]);
        pendingFiles.forEach((file: File) =>
          this.currentlyUploadingFiles.splice(
            this.currentlyUploadingFiles.indexOf(file),
            1
          )
        );
        this.filesLoading = false;
      },
      error: () => {
        this.removeFilesOffline(pendingFiles);
      },
    });
  }

  removeFilesOffline(filesToDelete: (File | LingFileEntity)[]): void {
    const newValue = this.control.value.filter(
      (file: any) => !filesToDelete.includes(file)
    );
    this.updateControlValue(newValue);
  }

  removeFilesOnline(
    filesToDelete: LingFileEntity[]
  ): Observable<LingFileEntity[]> {
    const uploadedIdsToDelete = filesToDelete.map(
      (file: LingFileEntity) => file._id!
    );
    return this.fileService.deleteMedia(uploadedIdsToDelete).pipe(
      tap((res) => {
        this.leftUploadedDuringThisInstance =
          this.leftUploadedDuringThisInstance.filter(
            (file) => !res.find((resItem) => resItem._id === file._id)
          );
        this.toBeDeleted = this.toBeDeleted.filter(
          (file) => !res.find((resItem) => resItem._id === file._id)
        );
      })
    );
  }

  updateControlValue(
    newValue: File[] | LingFileEntity[],
    emitEvent: boolean = true
  ): void {
    this.control.setValue(newValue, { emitEvent });
    this.onChange(this.control.value);
    this.valueChange.emit(this.control.value);
    this.updateLinksMap();
    this.cd.markForCheck();
  }

  onFileInputChange(event: any): void {
    this.onTouched();
    let newFiles = Array.from<File>(event.target.files);
    event.target.value = null;
    if (newFiles.length) {
      this.processFiles({ files: newFiles, operator: FileOperations.Add });
    }
  }

  markFileToBeDeleted(file: LingFileEntity): void {
    if (
      this.leftUploadedDuringThisInstance.includes(file) ||
      this.forceDelete
    ) {
      this.removeFilesOnline([file]).subscribe();
    } else {
      this.toBeDeleted.push(file);
    }
  }

  onRemoveFile(index: number): void {
    this.processFiles({
      files: [this.control.value[index]],
      operator: FileOperations.Delete,
    });
  }

  deleteAllCurrentUploadedFiles(): Observable<LingFileEntity[]> {
    const uploadedFiles = this.control.value.filter(
      (file: any) => !(file instanceof File)
    );
    return this.removeFilesOnline(uploadedFiles);
  }

  deleteAllMarkedFiles(): Observable<LingFileEntity[]> {
    return this.removeFilesOnline(this.toBeDeleted);
  }

  onRemoveButtonPressed(): void {
    this.removeButtonPress.emit();
  }

  openAddFileDialog(): void {
    if (this.disabledInput) {
      return;
    }
    if (!this.feathers.me$.value) {
      this.fileInputRef.nativeElement.click();
      return;
    }
    const dialogRef = this.matDialog.open(AddFileDialogComponent, {
      width: '560px',
      maxHeight: '80vh',
    });
    dialogRef.afterClosed().subscribe((result: LingFileEntity | undefined) => {
      if (result && this.canFit(1)) {
        this.processFiles({
          files: [result],
          operator: FileOperations.Add,
        });
      }
    });
  }

  onTabPressed(): void {
    if (this.isFocused) {
      this.isFocused = false;
    }
  }
}
